import axios from 'axios'
import React, {useCallback, useEffect} from 'react'
import clsx from 'clsx'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../../helpers'
import {HeaderUserMenu} from '../../../partials'
import NotificationDropdown from '../NotificationDropdown'
import {useReduxAuth} from '../../../../app/modules/auth'
import {USER_ROLES} from '../../../../app/constants/StatusConstants'
import {NotificationModel, ApiResponseModel} from '../../../../app/entities/entities'
import {API_ENDPOINTS} from '../../../../app/constants/apiConstants'
import {useHealthAlertNotification} from '../../../../app/modules/user/context/NotificationContext'
import {FiSettings} from 'react-icons/fi'
import {useNavigate} from 'react-router-dom'
import SessionManager from './SessionManager'
import HeaderMenuService from './HeaderMenuService'

const itemClass = 'ms-3 ms-lg-3'
const userAvatarClass = 'symbol-35px symbol-md-40px'

// Map numeric role_id to human-readable label
const ROLE_LABELS: Record<number, string> = {
  [USER_ROLES.ADMIN]: 'Admin',
  [USER_ROLES.DOCTOR]: 'Doctor',
  [USER_ROLES.NURSE]: 'Staff',
}

const Navbar: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const {healthAlertNotification, setHealthAlertNotification} = useHealthAlertNotification()
  const navigate = useNavigate()

  const handleClearAllNotifications = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.CLEAR_ALL_HEALTH_ALERTS())
      const allHealthAlerts = response.data?.data?.alerts || []
      setHealthAlertNotification(allHealthAlerts)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }
  const fetchAndUpdateNotifications = useCallback(async () => {
    try {
      const queryParams = `&get_notifications=${true}`
      const response = await axios.get<ApiResponseModel<{alerts: NotificationModel[]}>>(
        API_ENDPOINTS.GET_ALL_HEALTH_ALERTS(queryParams)
      )
      const allHealthAlerts = response.data?.data?.alerts || []
      setHealthAlertNotification(allHealthAlerts)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        console.error(error.message)
      } else {
        console.error('An unexpected error occurred')
      }
    }
  }, [setHealthAlertNotification])

  // fetch all notifications on load
  useEffect(() => {
    fetchAndUpdateNotifications()
  }, [fetchAndUpdateNotifications])

  const roleLabel =
    currentUser && ROLE_LABELS[currentUser.role_id] ? ROLE_LABELS[currentUser.role_id] : ''

  return (
    <div className='app-navbar flex-shrink-0'>
      {/* Hide notifications for Admin */}
      {currentUser?.role_id !== USER_ROLES.ADMIN && currentUser?.role_id !== USER_ROLES.BILLING_PERSONNEL && (
        <div className={clsx('app-navbar-item', itemClass, 'd-flex align-items-center gap-2')}>
          {/* Settings Button (left of notifications) */}
          <button
            className='btn btn-icon btn-custom btn-icon-muted'
            style={{width: 35, height: 35}}
            title='Settings'
            onClick={() => navigate('/settings')}
          >
            <FiSettings size={20} />
          </button>

          <NotificationDropdown
            notifications={healthAlertNotification}
            onClearNotifications={handleClearAllNotifications}
          />
        </div>
      )}

      {/* User info and menu */}
      <div className={clsx('app-navbar-item', itemClass, 'd-flex align-items-center')}>
        <div className='pe-4 d-flex flex-column'>
          <span
            className='fw-bolder fs-5 text-truncate d-block max-width'
            title={currentUser?.fullName || currentUser?.full_name}
          >
            {currentUser?.fullName || currentUser?.full_name}
          </span>
          {roleLabel && <span className='text-muted fs-7'>{roleLabel}</span>}
        </div>
        <div
          className={clsx('cursor-pointer symbol', userAvatarClass)}
          data-kt-menu-trigger='{default: "click"}'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
        >
          <img
            src={
              currentUser?.profile_image_pre_signed
                ? currentUser?.profile_image_pre_signed
                : toAbsoluteUrl('/media/avatars/blank.png')
            }
            // src={toAbsoluteUrl('/media/avatars/blank.png')}
            alt='user avatar'
            className='rounded-circle'
          />
        </div>
        <HeaderUserMenu />
      </div>
      
      {/* Session Management Components */}
      <SessionManager />
      <HeaderMenuService />
    </div>
  )
}

export {Navbar}
